/* External */
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';

export function useImpressionOnVisible({
  enabled = true,
  resetKey,
  threshold = 0.5,
  onImpression,
}) {
  const modalShow = useSelector((state) => state.modal.show);
  const modalShowRef = useRef(modalShow);
  modalShowRef.current = modalShow;
  const hasFiredRef = useRef(false);
  const onImpressionRef = useRef(onImpression);
  onImpressionRef.current = onImpression;
  const lastResetKeyRef = useRef(resetKey);
  const [element, setElement] = useState();

  const targetRef = useCallback((node) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      hasFiredRef.current = false;
    }

    if (!enabled || modalShow || !element || hasFiredRef.current) return () => {};

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFiredRef.current && !modalShowRef.current) {
          hasFiredRef.current = true;
          onImpressionRef.current();
          observer.disconnect();
        }
      },
      { threshold },
    );

    const startObserving = () => observer.observe(element);

    if (document.readyState === 'complete') {
      startObserving();
      return () => observer.disconnect();
    }

    window.addEventListener('load', startObserving, { once: true });
    return () => {
      window.removeEventListener('load', startObserving);
      observer.disconnect();
    };
  }, [element, enabled, modalShow, resetKey, threshold]);

  return targetRef;
}
